import React, {FC, memo, useCallback, useEffect, useMemo, useState} from "react";
import {ButtonBox, GridTable, LoadingBox, PrivilegesChecker, TextBox} from "..";
import {
  ActionTypeEnum,
  DictionaryModel,
  DictionaryResponseModel,
  LookupTypeEnum,
  PageEnum,
  RequestActionModel,
  ResponseBaseModel,
  RowStateEnum,
  SearchDictionaryRequestModel,
} from "../../models";
import {getAllDictionaries, saveDictionaries,} from "../../serviceBroker/dictionaryApiServiceBroker";
import {
  getLabelName,
  getSystemLanguageId,
  isArabicCurrentLanguage,
  reValidateLocalStorageCachedData
} from "../../utils";
import {TableColumn} from "react-data-table-component";

//#region internal component
const MemoizedTextBox = memo(({
                                initialValue,
                                onUpdate,
                                fieldName
                              }: {
  initialValue: string,
  onUpdate: (fieldName: string, value: string) => void,
  fieldName: string
}) => {
  // Local state to handle input value during typing
  const [inputValue, setInputValue] = useState(initialValue);
  // Update local state when initialValue changes
  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };
  const handleBlur = () => {
    // Only update parent state if value actually changed
    if (inputValue !== initialValue) {
      onUpdate(fieldName, inputValue);
    }
  };
  return (
      <TextBox
          inputValue={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          type="text"
          labelName={""}
          inputName={fieldName}
      />
  );
});
//#endregion
export const DictionaryList: FC<{
  getDictionaries: (searchObj: SearchDictionaryRequestModel) => void;
  request: DictionaryResponseModel[];
  onActionEvent: (o: RequestActionModel) => void;
  totalRows: number;
}> = ({ request, onActionEvent = () => {} }) => {
  //#region state
 // const [modifiedRows, setModifiedRows] = useState<{[key: string]: boolean}>({});
  const [dictionaries, setDictionaries] = useState<DictionaryResponseModel[]>();
  const [currentRequest, setCurrentRequest] = useState<DictionaryResponseModel[]>(request);
  const [searchKeyWord, setSearchKeyWord] = useState<string>("");
  const [_, setSearchDictionaryRequest] =
      useState<SearchDictionaryRequestModel>({
        pageNumber: 1,
        pageSize: 50,
        searchKeyWord: "",
        lang: getSystemLanguageId(),
      });
  const [loading, setLoading] = useState(false);
  //#endregion
  //#region functions
  const handleRowUpdate = useCallback((row: DictionaryResponseModel, fieldName: string, value: string) => {
    // Create a unique key for this row
    const                                 rowKey = `${row.ID}-${row.ControlType_ID}`;
    // setModifiedRows(prev => ({
    //   ...prev,
    //   [rowKey]: true
    // }));
    // Only update the specific row that changed
    setDictionaries(prevDictionaries =>
        prevDictionaries?.map(dict =>
            dict.ID === row.ID && dict.ControlType_ID === row.ControlType_ID
                ? { ...dict, [fieldName]: value, isChanged: true }
                : dict
        )
    );
  }, []);
  const handleSearch = useCallback(() => {
    const searchColumns = ["ArabicName", "EnglishName"];
    const searchTerm = searchKeyWord.trim().toLowerCase();
    if (!searchTerm) {
      setDictionaries(currentRequest);
      return;
    }
    const result = currentRequest.filter(item => {
      return searchColumns.some(key => {
        const value = item[key as keyof DictionaryResponseModel];
        return value && value.toString().toLowerCase().includes(searchTerm);
      });
    });
    setDictionaries(result);
    setSearchDictionaryRequest(prev => ({
      ...prev,
      searchKeyWord: searchKeyWord,
    }));
  }, [currentRequest, searchKeyWord]);
  const handleSave = async () => {
    setLoading(true);
    if (dictionaries && dictionaries.length > 0) {
      // Only save records that were changed
      const changedRecords = dictionaries.filter((x) => x.isChanged);

      if (changedRecords.length > 0) {
        const res: ResponseBaseModel<DictionaryModel> | null =
            await saveDictionaries(changedRecords);

        if (res != null && res.Errors != null && res.Errors.length !== 0) {
          onActionEvent({
            id: 0,
            action: ActionTypeEnum.Failed,
            request: res.Errors,
          });
        } else {
          onActionEvent({
            id: 0,
            action: ActionTypeEnum.Success,
            request: res?.Result,
          });

          await reValidateLocalStorageCachedData(LookupTypeEnum.DictionaryCacheKey);
          let result = await getAllDictionaries();
          let response: DictionaryResponseModel[] = [];

          if (result) {
            result.map((row, index) => {
              response.push({
                ArabicName: row.NameAr,
                EnglishName: row.NameEn,
                Name: row.Name,
                ID: row.Id,
                CreatedBy: 0,
                ModifiedBy: 0,
                rowState: RowStateEnum.Update,
                ControlType_ID: row.TypeId,
                CreationDate: undefined,
                ModificationDate: undefined,
                ShowToUser: undefined,
                System_ID: undefined,
                TranslatedName: undefined,
                VerifyOnUpdate: undefined,
                isChanged: false,
                index: index,
              });
            });

            setCurrentRequest(response);
            setDictionaries(response);
          }
        }
      }
    }
    setLoading(false);
  };
  //#endregion
  //#region variables
  const isArabic = isArabicCurrentLanguage();
  const columns = useMemo<TableColumn<DictionaryResponseModel>[]>(() => [
    {
      name: getLabelName("Control"),
      selector: (_row) => _row.Name,
      sortable: true,
    },
    {
      name: getLabelName("Arabic Name"),
      cell: (row: DictionaryResponseModel) => (
          <MemoizedTextBox
              initialValue={row.ArabicName}
              onUpdate={(fieldName, value) => handleRowUpdate(row, fieldName, value)}
              fieldName="ArabicName"
          />
      ),
    },
    {
      name: getLabelName("English Name"),
      cell: (row: DictionaryResponseModel) => (
          <MemoizedTextBox
              initialValue={row.EnglishName}
              onUpdate={(fieldName, value) => handleRowUpdate(row, fieldName, value)}
              fieldName="EnglishName"
          />
      ),
    },
  ], [handleRowUpdate]);
  //#endregion
  //#region useEffect
  useEffect(() => {
    const fillData = async () => {
      setLoading(true);
      setDictionaries(request);
      setCurrentRequest(request);
      setLoading(false);
    };
    fillData().then(() => {});
  }, [request]);
  //#endregion
  //#region html
  return (
    <>
      {loading && <LoadingBox />}
      <div
        // className="col-12 d-flex justify-content-start my-4 mt-0"
        className="row my-4 mt-0"
      >
        <div className="col-12 col-md-8">
          <TextBox
            labelName={"Search"}
            inputName={""}
            inputValue={searchKeyWord}
            onChange={(e: any) => {
              setSearchKeyWord(e.target.value);
            }}
          />
        </div>
        <div className="col-12 col-md-4 d-flex flex-wrap align-items-end gap-2">
          <PrivilegesChecker formId={PageEnum.Dictionary} action="EnableSearch">
            <ButtonBox
              iconType="account-search"
              className="btn-gradient-primary mx-3"
              size="sm"
              onClick={handleSearch}
            >
              {getLabelName("search")}
            </ButtonBox>
          </PrivilegesChecker>
          <PrivilegesChecker formId={PageEnum.Dictionary} action="EnableSave">
            <ButtonBox
              iconType="content-save"
              type="submit"
              className="btn  btn-sm mx-2"
              variant="primary"
              disabled={!dictionaries || dictionaries.length == 0}
              onClick={handleSave}
            >
              {getLabelName("Save")}
            </ButtonBox>
          </PrivilegesChecker>
        </div>
      </div>
      {dictionaries != null && dictionaries.length !== 0 && (
        <div className="mt-4">
          <GridTable
            columns={columns}
            data={dictionaries}
            totalRows={dictionaries?.length ?? 0}
            currentPage={1}
            pageSize={100}
            paginationType="client"
            columnsProps={{ onActionEvent, isArabic, actions: [] }}
          />
        </div>
      )}
    </>
  );
  //#endregion
};